// @ts-nocheck
import React from 'react';
import { AlertTriangle, Copy, Play, Pause, Archive, Mail } from 'lucide-react';
import ConfirmationDialog from '../support/ConfirmationDialog';
import ConfirmationDialogNew from 'components/ui/confirmationDialog';
import { tone, toneForStatus } from "components/ui/tone";

// Bulk Delete Confirmation Dialog
export const BulkDeleteCampaignDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  selectedCampaigns = [],
  isLoading = false 
}) => {
  const campaignCount = selectedCampaigns.length;
  const activeCampaigns = selectedCampaigns.filter(c => c.status === 'active');
  const hasActiveCampaigns = activeCampaigns.length > 0;

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Delete ${campaignCount} Campaign${campaignCount !== 1 ? 's' : ''}?`}
      type="danger"
      confirmText="Delete Campaigns"
      isLoading={isLoading}
      requireConfirmation={hasActiveCampaigns}
      confirmationText={hasActiveCampaigns ? 'DELETE' : ''}
    >
      <div className="space-y-4">
        <p className="text-gray-700 dark:text-gray-300">
          Are you sure you want to delete {campaignCount} campaign{campaignCount !== 1 ? 's' : ''}? 
          This action cannot be undone.
        </p>
        
        {hasActiveCampaigns && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-1">
                  Warning: Active Campaigns
                </p>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {activeCampaigns.length} of the selected campaigns {activeCampaigns.length === 1 ? 'is' : 'are'} currently active. 
                  Deleting active campaigns will immediately stop all email sending and cannot be reversed.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
          <h4 className="font-medium text-content dark:text-white mb-2">Selected campaigns:</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedCampaigns.slice(0, 5).map(campaign => (
              <div key={campaign.id} className="flex items-center gap-2 text-sm">
                <Mail className="w-3 h-3 text-content-muted dark:text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{campaign.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  campaign.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                  campaign.status === 'paused' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                  campaign.status === 'draft' ? 'bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-300' :
                  'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                }`}>
                  {campaign.status}
                </span>
              </div>
            ))}
            {selectedCampaigns.length > 5 && (
              <p className="text-sm text-content-muted dark:text-gray-400">
                ...and {selectedCampaigns.length - 5} more campaigns
              </p>
            )}
          </div>
        </div>

        <p className="text-sm text-content-secondary dark:text-gray-400">
          All campaign data, including performance metrics and history, will be permanently deleted.
        </p>
      </div>
    </ConfirmationDialog>
  );
};

// Single Campaign Delete Confirmation Dialog
export const DeleteCampaignDialog = ({
  isOpen,
  onClose,
  onConfirm,
  campaign,
  isLoading = false,
}) => {
  if (!campaign) return null;

  const isActive = campaign.status === "active";

  return (
    <ConfirmationDialogNew
      open={isOpen}
      onCancel={onClose}
      onConfirm={onConfirm}
      title="Delete Campaign?"
      description={
        isActive
          ? "This campaign is currently active. Deleting it will immediately stop all email sending and cannot be reversed."
          : "Are you sure you want to delete this campaign? This action cannot be undone."
      }
      confirmLabel="Delete Campaign"
      cancelLabel="Cancel"
      variant="destructive"
      isLoading={isLoading}
      size="sm"
      align="center"
    >
      {/* Optional rich details area */}
      <div className="flex flex-col gap-4 mt-4 text-gray-700 dark:text-gray-300">
        {/* Campaign summary */}
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-content-secondary" />
            <span className="font-medium text-content">{campaign.name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-content-secondary flex-wrap">
            <span
              className={`px-2 py-0.5 rounded-full text-xs ${
                campaign.status === "active"
                  ? "bg-green-100 text-green-700"
                  : campaign.status === "paused"
                  ? "bg-yellow-100 text-yellow-700"
                  : campaign.status === "draft"
                  ? "bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300"
                  : "bg-purple-100 text-purple-700"
              }`}
            >
              {campaign.status}
            </span>
            <span>•</span>
            <span>{campaign.segmentName || "No segment"}</span>
            <span>•</span>
            <span>
              {(campaign.estimatedReach || 0).toLocaleString()} recipients
            </span>
          </div>
        </div>

        {/* Warning if active */}
        {isActive && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-300 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-1">
                  Warning: Active Campaign
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  Deleting this campaign will immediately stop all email sending
                  and cannot be undone.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info note */}
        <p className="text-sm text-content-secondary">
          All campaign data, including performance metrics and history, will be
          permanently deleted.
        </p>
      </div>
    </ConfirmationDialogNew>
  );
};


// Bulk Status Change Confirmation Dialog
export const BulkStatusChangeDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  selectedCampaigns = [],
  newStatus,
  isLoading = false 
}) => {
  const campaignCount = selectedCampaigns.length;
  
  const getStatusInfo = (status) => {
    // Copy and icons stay; the three colour fields join the shared mapping.
    const INFO = {
      active: { title: 'Activate Campaigns', icon: Play, color: tone(toneForStatus('active')).text, bgColor: tone(toneForStatus('active')).surface, borderColor: tone(toneForStatus('active')).border, action: 'activate', description: 'These campaigns will start sending emails according to their schedules.' },
      paused: { title: 'Pause Campaigns', icon: Pause, color: tone(toneForStatus('paused')).text, bgColor: tone(toneForStatus('paused')).surface, borderColor: tone(toneForStatus('paused')).border, action: 'pause', description: 'These campaigns will stop sending emails but can be resumed later.' },
      archived: { title: 'Archive Campaigns', icon: Archive, color: tone(toneForStatus('archived')).text, bgColor: tone(toneForStatus('archived')).surface, borderColor: tone(toneForStatus('archived')).border, action: 'archive', description: 'These campaigns will be moved to archived status and hidden from the main view.' },
    };
    return INFO[status] ?? INFO['active'];
  };

  const statusInfo = getStatusInfo(newStatus);
  const Icon = statusInfo.icon;

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={statusInfo.title}
      type="info"
      confirmText={`${statusInfo.action.charAt(0).toUpperCase() + statusInfo.action.slice(1)} ${campaignCount} Campaign${campaignCount !== 1 ? 's' : ''}`}
      isLoading={isLoading}
    >
      <div className="space-y-4">
        <p className="text-gray-700 dark:text-gray-300">
          Are you sure you want to {statusInfo.action} {campaignCount} campaign{campaignCount !== 1 ? 's' : ''}?
        </p>
        
        <div className={`${statusInfo.bgColor} ${statusInfo.borderColor} border rounded-lg p-3`}>
          <div className="flex items-start gap-2">
            <Icon className={`w-5 h-5 ${statusInfo.color} mt-0.5 flex-shrink-0`} />
            <p className={`text-sm ${statusInfo.color.replace('600', '800')}`}>
              {statusInfo.description}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
          <h4 className="font-medium text-content dark:text-white mb-2">Selected campaigns:</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedCampaigns.slice(0, 5).map(campaign => (
              <div key={campaign.id} className="flex items-center gap-2 text-sm">
                <Mail className="w-3 h-3 text-content-muted dark:text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{campaign.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  campaign.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                  campaign.status === 'paused' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                  campaign.status === 'draft' ? 'bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-300' :
                  'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                }`}>
                  {campaign.status}
                </span>
              </div>
            ))}
            {selectedCampaigns.length > 5 && (
              <p className="text-sm text-content-muted dark:text-gray-400">
                ...and {selectedCampaigns.length - 5} more campaigns
              </p>
            )}
          </div>
        </div>
      </div>
    </ConfirmationDialog>
  );
};

// Bulk Duplicate Confirmation Dialog
export const BulkDuplicateDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  selectedCampaigns = [],
  isLoading = false 
}) => {
  const campaignCount = selectedCampaigns.length;

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Duplicate ${campaignCount} Campaign${campaignCount !== 1 ? 's' : ''}?`}
      type="info"
      confirmText="Create Duplicates"
      isLoading={isLoading}
    >
      <div className="space-y-4">
        <p className="text-gray-700 dark:text-gray-300">
          This will create {campaignCount} new campaign{campaignCount !== 1 ? 's' : ''} based on the selected campaign{campaignCount !== 1 ? 's' : ''}.
        </p>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Copy className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-1">
                What will be duplicated:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li>• Campaign settings and configuration</li>
                <li>• Email template and content</li>
                <li>• Audience segment selection</li>
                <li>• AI agent configuration (if applicable)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
          <h4 className="font-medium text-content dark:text-white mb-2">Campaigns to duplicate:</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedCampaigns.slice(0, 5).map(campaign => (
              <div key={campaign.id} className="flex items-center gap-2 text-sm">
                <Mail className="w-3 h-3 text-content-muted dark:text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{campaign.name}</span>
                <span className="text-content-muted dark:text-gray-500">→</span>
                <span className="text-content-secondary dark:text-gray-400">{campaign.name} (Copy)</span>
              </div>
            ))}
            {selectedCampaigns.length > 5 && (
              <p className="text-sm text-content-muted dark:text-gray-400">
                ...and {selectedCampaigns.length - 5} more campaigns
              </p>
            )}
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/40 rounded-lg p-3">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            <strong>Note:</strong> Duplicated campaigns will be created in "draft" status and will need to be reviewed before activation.
          </p>
        </div>
      </div>
    </ConfirmationDialog>
  );
};